<template>
  <transition name="cc-fade">
    <div
      v-if="visible"
      class="cc-banner"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie-toestemming"
    >
      <div class="cc-inner">
        <div class="cc-text">
          <p class="cc-title">Wij gebruiken cookies</p>
          <p class="cc-body">
            We plaatsen functionele cookies voor de webshop en - met jouw toestemming -
            analytische en marketingcookies om onze site te verbeteren en relevante
            advertenties te tonen. Meer in ons
            <router-link to="/cookies" class="cc-link">cookiebeleid</router-link>.
          </p>
        </div>
        <div class="cc-actions">
          <button type="button" class="cc-btn cc-btn--ghost" @click="decline">
            Alleen noodzakelijk
          </button>
          <button type="button" class="cc-btn cc-btn--primary" @click="accept">
            Alles accepteren
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, onMounted } from 'vue'

const visible = ref(false)
const STORAGE_KEY = 'p2c_cookie_consent'

// Consent-signaal doorgeven aan Google (Consent Mode v2) en Microsoft Clarity.
function applyConsent(granted) {
  if (typeof window === 'undefined') return
  const value = granted ? 'granted' : 'denied'
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      ad_storage:         value,
      ad_user_data:       value,
      ad_personalization: value,
      analytics_storage:  value,
    })
  }
  if (granted && typeof window.clarity === 'function') {
    try { window.clarity('consent') } catch (e) { /* noop */ }
  }
}

function accept() {
  try { localStorage.setItem(STORAGE_KEY, 'granted') } catch (e) { /* noop */ }
  applyConsent(true)
  visible.value = false
}

function decline() {
  try { localStorage.setItem(STORAGE_KEY, 'denied') } catch (e) { /* noop */ }
  applyConsent(false)
  visible.value = false
}

onMounted(() => {
  // SSR-veilig: alleen client-side beslissen of de banner getoond wordt.
  let choice = null
  try { choice = localStorage.getItem(STORAGE_KEY) } catch (e) { /* noop */ }
  if (choice !== 'granted' && choice !== 'denied') {
    visible.value = true
  }
})
</script>

<style scoped>
.cc-banner {
  position: fixed;
  left: 1rem;
  right: 1rem;
  bottom: 1rem;
  z-index: 60;
  display: flex;
  justify-content: center;
  pointer-events: none;
}
.cc-inner {
  pointer-events: auto;
  max-width: 960px;
  width: 100%;
  background: #111;
  color: #fff;
  border-radius: 14px;
  padding: 1.125rem 1.25rem;
  display: flex;
  align-items: center;
  gap: 1.25rem;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
}
.cc-title { font-size: 0.95rem; font-weight: 700; margin: 0 0 0.25rem; }
.cc-body {
  font-size: 0.82rem;
  line-height: 1.55;
  color: #cfcfcf;
  margin: 0;
  max-width: 640px;
}
.cc-link { color: #FFD500; text-underline-offset: 2px; }
.cc-actions { display: flex; gap: 0.625rem; flex-shrink: 0; }
.cc-btn {
  border: none;
  cursor: pointer;
  font-weight: 700;
  font-size: 0.85rem;
  padding: 0.7rem 1.1rem;
  border-radius: 9px;
  white-space: nowrap;
  transition: background 0.15s, transform 0.15s, box-shadow 0.15s;
}
.cc-btn--primary { background: #FFD500; color: #111; }
.cc-btn--primary:hover { background: #ffb800; transform: translateY(-1px); }
.cc-btn--primary:active { transform: translateY(0) scale(0.99); }
.cc-btn--ghost {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.25);
}
.cc-btn--ghost:hover { background: rgba(255, 255, 255, 0.18); }
.cc-btn:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(255, 213, 0, 0.5); }

.cc-fade-enter-active,
.cc-fade-leave-active { transition: opacity 0.25s ease, transform 0.25s ease; }
.cc-fade-enter-from,
.cc-fade-leave-to { opacity: 0; transform: translateY(12px); }

@media (max-width: 720px) {
  .cc-inner { flex-direction: column; align-items: stretch; gap: 0.875rem; }
  .cc-actions { justify-content: stretch; }
  .cc-btn { flex: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .cc-fade-enter-active,
  .cc-fade-leave-active { transition: none; }
}
</style>
